import { MedChronExportMenu } from "@/components/MedicalChronology/MedChronExportMenu";
import { EditableTitle } from "@/components/shared/EditableTitle";
import { useElementWidth } from "@/hooks/useElementWidth";
import { useCanMarkCaseReviewed } from "@/permissions/v2/cases";
import { cn } from "@paribus/shared-ui";
import { Check, ClipboardList, Settings } from "lucide-react";
import React from "react";
import type { NavigateFunction } from "react-router-dom";

import type { FirmContext } from "../../contexts/AuthContext";
import { canCustomizeReports } from "../../permissions/organizations";
import type { MedChronFirmContext } from "./types";

const ICON_ONLY_MAX_WIDTH = 860;

export interface MedicalChronologyHeaderViewProps {
  caseId: string;
  caseName: string | null;
  physicianName: string | null;
  caseStatusId: number | null;
  firmContext: MedChronFirmContext | undefined;
  onMarkReviewed: () => void;
  templateId: string | null;
  navigate: NavigateFunction;
  selectedVersionId: string | null;
  onCustomizeExport: () => void;
  onCaseNameSaved: (newCaseName: string) => Promise<void>;
}

export const MedicalChronologyHeaderView: React.FC<MedicalChronologyHeaderViewProps> = ({
  caseId,
  caseName,
  physicianName,
  caseStatusId,
  firmContext,
  onMarkReviewed,
  templateId,
  navigate,
  selectedVersionId,
  onCustomizeExport,
  onCaseNameSaved,
}) => {
  const canShowCustomizeReports = canCustomizeReports(firmContext as FirmContext | undefined);
  const canMarkReviewed = useCanMarkCaseReviewed(firmContext?.firmId);
  /**
   * Intentional Container/Presenter exception: this view measures its own rendered
   * row so it can react to container width rather than viewport breakpoints.
   */
  const [rowRef, rowWidth] = useElementWidth<HTMLDivElement>();
  const isCompact = rowWidth < ICON_ONLY_MAX_WIDTH;

  return (
    <div className="mb-2 border-b border-neutral-200 pb-3 pt-1">
      <div ref={rowRef} className={cn("flex items-center gap-2.5", !isCompact && "gap-3")}>
        {/* Icon */}
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200/80",
            !isCompact && "h-10 w-10"
          )}
        >
          <ClipboardList size={isCompact ? 20 : 22} strokeWidth={2} aria-hidden />
        </div>

        {/* Name + physician badge — flex-1 keeps buttons on the same row; truncate with title tooltip */}
        <div className="min-w-0 flex-1">
          <EditableTitle
            value={caseName ?? ""}
            onSave={onCaseNameSaved}
            as="h2"
            sizeClassName="text-2xl"
            placeholder="Medical Chronology"
            editAriaLabel="Edit case name"
            nativeTitleAttribute
            wrapperClassName="flex min-w-0 items-center gap-1.5"
          />
          {physicianName && (
            <span className="mt-0.5 inline-flex max-w-full items-center truncate rounded-full bg-primary-subtle px-2 py-0.5 text-2xs font-semibold uppercase text-primary-emphasis ring-1 ring-inset ring-primary-border/80">
              Physician: {physicianName}
            </span>
          )}
        </div>

        <div className={cn("flex shrink-0 items-center gap-1.5", !isCompact && "gap-2")}>
          {canMarkReviewed && caseStatusId === 2 && (
            <button
              type="button"
              aria-label="Mark as Finished"
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-md transition-colors duration-200 hover:bg-primary-hover focus-visible:shadow-focus",
                !isCompact && "h-11 w-auto gap-2 px-5"
              )}
              onClick={onMarkReviewed}
            >
              <Check className="h-4 w-4 shrink-0" aria-hidden />
              {!isCompact && <span className="text-sm font-bold">Mark as Finished</span>}
            </button>
          )}

          {canShowCustomizeReports && (
            <button
              type="button"
              aria-label="Customize reports"
              className={cn(
                "group flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-card shadow-sm ring-1 ring-neutral-200 transition-colors duration-200 hover:bg-neutral-50 hover:ring-neutral-300 focus-visible:shadow-focus disabled:opacity-50",
                !isCompact && "h-11 w-auto gap-3 px-4"
              )}
              onClick={() => templateId && physicianName && navigate(`/templates/${templateId}`)}
              disabled={!templateId || !physicianName}
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-neutral-500 transition-colors group-hover:bg-primary-subtle group-hover:text-primary">
                <Settings size={16} strokeWidth={2.5} aria-hidden />
              </div>
              {!isCompact && (
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-sm font-bold text-neutral-900">Customize reports</span>
                  {physicianName && (
                    <span className="text-2xs font-medium text-primary">
                      Tailor for {physicianName}
                    </span>
                  )}
                </div>
              )}
            </button>
          )}

          {selectedVersionId && (
            <MedChronExportMenu
              variant="header"
              caseId={caseId}
              caseName={caseName}
              isCompact={isCompact}
              onCustomize={onCustomizeExport}
            />
          )}
        </div>
      </div>
    </div>
  );
};
